import React, { useContext, useState, useEffect } from 'react';
import { ShoppingCart, Zap, TrendingUp, Wallet } from 'lucide-react';
import axios from 'axios';
import { ApiLink } from '../../APILink/ApiLink';
import authHeader from '../../Auth/authHeader';
import { FiscalYearContext } from '../../FiscalYear/FiscalYearContext';
import { errorAlert } from '../../Components/Alerts';
import KPICard, { KPICardSkeleton } from './DashboardComponents/KPICard';
import NewChart from './DashboardComponents/NewChart';
import BankCashPanel from './DashboardComponents/BankCashPanel';
import RecentTransactions from './DashboardComponents/RecentTransactions';
import ExpensePieChart from './DashboardComponents/ExpensePieChart';
import { getActionTypeLabel } from '../../OtherData/actionTypeMap';
import './DashboardPage.scss';

export default function DashboardPage() {
  const [salesTotal, setSalesTotal] = useState(0);
  const [purchaseTotal, setPurchaseTotal] = useState(0);
  const [investmentTotal, setInvestmentTotal] = useState(0);
  const [expenseTotal, setExpenseTotal] = useState(0);
  const [salesTrend, setSalesTrend] = useState(0);
  const [purchaseTrend, setPurchaseTrend] = useState(0);
  const [investmentTrend, setInvestmentTrend] = useState(0);
  const [expenseTrend, setExpenseTrend] = useState(0);
  const [salesBreakdown, setSalesBreakdown] = useState([]);
  const [purchaseBreakdown, setPurchaseBreakdown] = useState([]);
  const [investmentBreakdown, setInvestmentBreakdown] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [loading, setLoading] = useState(true);

  const { fiscalYear } = useContext(FiscalYearContext);
  const headers = authHeader();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [
        totalsResponse,
        trendsResponse,
        salesBreakdownResponse,
        purchaseBreakdownResponse,
        investmentBreakdownResponse,
        expenseBreakdownResponse
      ] = await Promise.all([
        axios.get(
          ApiLink.API_URL + 'Dashboard/totalValues',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        ),
        axios.get(
          ApiLink.API_URL + 'Dashboard/totalValuesYtd',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        ),
        axios.get(
          ApiLink.API_URL + 'Dashboard/salesBreakdown',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        ),
        axios.get(
          ApiLink.API_URL + 'Dashboard/purchaseBreakdown',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        ),
        axios.get(
          ApiLink.API_URL + 'Dashboard/investmentBreakdown',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        ),
        axios.get(
          ApiLink.API_URL + 'Dashboard/expenseBreakdown',
          {
            headers,
            params: { fiscalYear: fiscalYear }
          }
        )
      ]);

      if (totalsResponse.data && totalsResponse.data.length > 0) {
        const data = totalsResponse.data[0];
        setSalesTotal(data.TotalSales || 0);
        setPurchaseTotal(data.PurchaseOrders || 0);
        setInvestmentTotal(data.TotalInvestments || 0);
        setExpenseTotal(data.TotalExpenses || 0);
      }

      if (trendsResponse.data && trendsResponse.data.length > 0) {
        const trendData = trendsResponse.data[0];
        setSalesTrend(Number(trendData.SalesTrend) || 0);
        setPurchaseTrend(Number(trendData.PurchaseOrdersTrend) || 0);
        setInvestmentTrend(Number(trendData.InvestmentsTrend) || 0);
        setExpenseTrend(Number(trendData.ExpensesTrend) || 0);
      }

      if (Array.isArray(salesBreakdownResponse.data)) {
        const breakdownItems = salesBreakdownResponse.data.map((row) => ({
          actionType: row.ActionType,
          amount: Number(row.Amount) || 0
        }));
        setSalesBreakdown(breakdownItems);
      } else {
        setSalesBreakdown([]);
      }

      if (Array.isArray(purchaseBreakdownResponse.data)) {
        const breakdownItems = purchaseBreakdownResponse.data.map((row) => ({
          actionType: row.ActionType,
          amount: Number(row.Amount) || 0
        }));
        setPurchaseBreakdown(breakdownItems);
      } else {
        setPurchaseBreakdown([]);
      }

      if (Array.isArray(investmentBreakdownResponse.data)) {
        const breakdownItems = investmentBreakdownResponse.data.map((row) => ({
          actionType: row.ActionType,
          amount: Number(row.Amount) || 0
        }));
        setInvestmentBreakdown(breakdownItems);
      } else {
        setInvestmentBreakdown([]);
      }

      if (Array.isArray(expenseBreakdownResponse.data)) {
        const breakdownItems = expenseBreakdownResponse.data.map((row) => ({
          ledgerId: Number(row.LedgerID),
          amount: Number(row.Amount) || 0
        }));
        setExpenseBreakdown(breakdownItems);
      } else {
        setExpenseBreakdown([]);
      }
    } catch (error) {
      errorAlert('Gabim në kërkesë:', error);
      if (error.response?.data?.errorMessage) {
        errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
      } else if (error.message) {
        errorAlert(`Gabim: ${error.message}`);
      } else {
        errorAlert('Gabim i panjohur në kërkesë. Shih konsolën për detaje.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fiscalYear]);

  const formatNumber = (num) => {
    return num ? num.toLocaleString('en-US', { minimumFractionDigits: 0 }) : '0.00';
  };

  const expenseCategoryLabelMap = {
    601: 'Shpenzimet e Zhvleresimit/Amortizimit',
    602: 'Shpenzimet e Financiare (Interesi/Provizioni)',
    603: 'Shpenzimet e Pagave',
    604: 'Shpenzimet e Kontributeve ne Paga',
    605: 'Shpenzimet e Tatimit ne Paga',
    606: 'Shpenzimet e Tatimit Vjetor',
    607: 'Shpenzimet e Qirase dhe Lizingut',
    608: 'Shpenzimet e Tatimit ne Qira',
    609: 'Shpenzimet Operative'
  };

  const salesBreakdownItems = salesBreakdown.map((item) => ({
    label: getActionTypeLabel(item.actionType),
    value: `€ ${formatNumber(item.amount)}`
  }));

  const purchaseBreakdownItems = purchaseBreakdown.map((item) => ({
    label: getActionTypeLabel(item.actionType),
    value: `€ ${formatNumber(item.amount)}`
  }));

  const investmentBreakdownItems = investmentBreakdown.map((item) => ({
    label: getActionTypeLabel(item.actionType),
    value: `€ ${formatNumber(item.amount)}`
  }));

  const expenseBreakdownItems = expenseBreakdown.map((item) => ({
    label: expenseCategoryLabelMap[item.ledgerId] || 'Shpenzime te tjera',
    value: `€ ${formatNumber(item.amount)}`
  }));

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="kpi-grid">
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
          <KPICardSkeleton />
        </div>

        <div className="dashboard-chart-row">
          <NewChart />
          <BankCashPanel />
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <div className="kpi-grid">
        <KPICard
          title="Shitjet Totale"
          value={`€ ${formatNumber(salesTotal)}`}
          icon={ShoppingCart}
          color="green"
          trend={salesTrend}
          trendLabel="vitit kaluar"
          to="/Sales/salesindetails"
          linkLabel="Lista Shitjeve"
          infoTitle="Përbërja e shitjeve"
          infoButtonLabel="Shiko përbërjen"
          infoItems={salesBreakdownItems}
        />
        <KPICard
          title="Blerjet"
          value={`€ ${formatNumber(purchaseTotal)}`}
          icon={TrendingUp}
          color="orange"
          trend={purchaseTrend}
          trendLabel="vitit kaluar"
          to="/purchases/detailpurchase"
          linkLabel="Lista Blerjeve"
          infoTitle="Përbërja e blerjeve"
          infoButtonLabel="Shiko përbërjen"
          infoItems={purchaseBreakdownItems}
        />
        <KPICard
          title="Blerjet Investive"
          value={`€ ${formatNumber(investmentTotal)}`}
          icon={Zap}
          color="blue"
          trend={investmentTrend}
          trendLabel="vitit kaluar"
          to="/purchases/investmentpurchases"
          linkLabel="Lista Blerjeve Investive"
          infoTitle="Përbërja e blerjeve investive"
          infoButtonLabel="Shiko përbërjen"
          infoItems={investmentBreakdownItems}
        />
        <KPICard
          title="Shpenzimet"
          value={`€ ${formatNumber(expenseTotal)}`}
          icon={Wallet}
          color="red"
          trend={expenseTrend}
          trendLabel="vitit kaluar"
          to="/expences/expensepurchases"
          linkLabel="Lista Shpenzimeve"
          infoTitle="Përbërja e shpenzimeve"
          infoButtonLabel="Shiko përbërjen"
          infoItems={expenseBreakdownItems}
        />
      </div>

      <div className="dashboard-chart-row">
        <NewChart />
        <BankCashPanel />
      </div>

      <div className="dashboard-bottom-row">
        <RecentTransactions />
        <ExpensePieChart />
      </div>
    </div>
  );
}